<template>
  <section class="container py-5">
    <div v-if="brokerOrigins.length > 1" class="row justify-content-center">
      <div class="col-md-4">
        <select v-model="brokerOrigin" name="brokerOrigin" id="brokerOrigin" class="form-control">
          <option v-for="origin in brokerOrigins" :key="origin.value" :value="origin.value">
            {{ origin.name }}
          </option>
        </select>
      </div>
    </div>
    <div v-if="loading || aspspLoading"><LoaderPic />Loading...</div>
    <div v-else-if="allStatuses && Object.keys(allStatuses).length">
      <div v-for="cc in Object.keys(allStatuses).sort((a, b) => countriesList[a].localeCompare(countriesList[b]))" :key="cc">
        <div class="navbar">
          <a :href="`#${cc}`" @click.prevent="toggleCountry(cc)" class="navbar-brand col-4">
            <img :src="require(`../assets/countries/${cc}.svg`)" alt="" class="flag mr-2" />
            {{ countriesList[cc] }}
          </a>
          <span>
            {{ (aspspsCount = Object.keys(allStatuses[cc]).length) }} ASPSP{{ aspspsCount != 1 ? 's' : '' }} monitored
          </span>
          <span>
            {{ (issuesCount = getIssuesCount(cc)) }} issue{{ `${issuesCount != 1 ? 's' : ''}` }} detected
          </span>
          <span v-if="subscriptionEnabled">
            <ToggleSwitch v-model="isSubscribed[cc]" @change="handleSubscription($event, cc)"/>
          </span>
        </div>
        <ul class="list-group" v-if="expendedCountries[cc] === true">
          <li v-if="countryLoading[cc]" class="list-group-item"><LoaderPic />Loading...</li>
          <template v-else>
            <li v-for="brand in Object.keys(allStatuses[cc]).sort()" :key="brand" class="list-group-item">
              <div
                class="d-flex justify-content-between align-items-center mb-2 brand-header"
                @click="toggleBrand(cc, brand)"
              >
                <h5 class="mb-0">{{ brand }}</h5>
                <span class="d-flex align-items-center brand-toggle-arrow">
                  Reported Disruptions {{ expandedBrands[`${cc}-${brand}`] ? '▲' : '▼' }}
                </span>
              </div>
              <div v-for="service in Object.keys(allStatuses[cc][brand]).sort()" :key="service">
                <div v-for="psuType in Object.keys(allStatuses[cc][brand][service]).sort()" :key="psuType" class="row status-row">
                  <div class="col-2">
                    {{ `${psuType} ${serviceNames[service]}` }}
                  </div>
                  <div class="col-1">
                    <font-awesome-icon class="traffic-icon" :icon="getTrafficStatusesIcon(cc, brand, service, psuType)" v-b-popover.hover.focus.top="`${getTrafficStatuses(cc, brand, service, psuType)} traffic`.toLocaleLowerCase()">
                    </font-awesome-icon>
                  </div>
                  <div class="col-6">
                    <template v-for="(s, d) in getHistoricStatusesByDate(cc, brand, service, psuType)">
                      <span
                        class="badge mr-1 historic-status"
                        :key="`${cc}-${brand}-${service}-${psuType}-${d}-span`"
                        :class="`bg-${statusClasses[s.status]}`"
                        :title="d"
                        :id="`historic-status-${cc}-${brand}-${service}-${psuType}-${d}`"
                        @mouseover="handleMouseEnter(`${cc}-${brand}-${service}-${psuType}-${d}`)"
                        @mouseleave="handleMouseLeave"
                        tabindex="0"
                      >&nbsp;</span>
                      <b-popover
                        v-if="hoveredItem === `${cc}-${brand}-${service}-${psuType}-${d}`"
                        :key="`${cc}-${brand}-${service}-${psuType}-${d}-popover`"
                        :target="`historic-status-${cc}-${brand}-${service}-${psuType}-${d}`"
                        triggers="hover focus"
                        placement="top"
                        :title="d"
                      >
                        {{ statusDescriptions[s.status] }}
                      </b-popover>
                    </template>
                  </div>
                  <div class="col-3">
                    <span class="badge status-badge" :class="`bg-${statusClasses[allStatuses[cc][brand][service][psuType].status]}`">{{
                      statusDescriptions[allStatuses[cc][brand][service][psuType].status]
                    }}</span>
                  </div>
                </div>
              </div>
              <RecentDisruptions
                v-if="expandedBrands[`${cc}-${brand}`]"
                :loading="!!recentDisruptionsLoading[`${cc}-${brand}`]"
                :disruptions="getBrandRecentDisruptions(cc, brand)"
                class="mt-2"
              />
            </li>
          </template>
        </ul>
      </div>
    </div>
    <div v-else-if="allStatuses && !Object.keys(allStatuses).length" class="mt-5">No data</div>
    <div v-else class="mt-5">Unable to fetch statuses</div>
  </section>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import metadata from '../mixins/metadata.js'
import LoaderPic from '../components/elements/Loader/LoaderPic.vue'
import { BPopover } from 'bootstrap-vue/src/components/popover'
import ToggleSwitch from '../components/ToggleSwitch.vue'
import RecentDisruptions from '../components/RecentDisruptions.vue'

export default {
  name: 'Aspsps',
  mixins: [metadata],
  data() {
    return {
      aspspLoading: false,
      allStatuses: null,
      allTrafficStatuses: null,
      allHistoricStatuses: {},
      countryLoading: {},
      expendedCountries: {},
      hoveredItem: null,
      statusDescriptions: {
        green: 'no issues detected',
        yellow: 'possible issues detected',
        red: 'major issues detected',
        grey: 'no data'
      },
      statusClasses: {
        green: 'success',
        yellow: 'warning',
        red: 'danger',
        grey: 'secondary'
      },
      serviceNames: {
        AIS: 'accounts',
        PIS: 'payments'
      },
      statusOrder: {
        grey: 0,
        green: 1,
        yellow: 2,
        red: 3
      },
      expandedBrands: {},
      recentDisruptions: {},
      recentDisruptionsLoading: {},
      brokerOrigin: localStorage.getItem('selected_broker') || '',
      isSubscribed: {},
      subscriptionIds: {}
    }
  },
  watch: {
    brokerOrigin: {
      handler: function (brokerOrigin) {
        localStorage.setItem('selected_broker', brokerOrigin)
        this.getUserSubscriptions(brokerOrigin)
        this.updateStatuses(brokerOrigin)
      },
      immediate: true
    }
  },
  components: {
    LoaderPic,
    BPopover,
    ToggleSwitch,
    RecentDisruptions
  },
  computed: {
    ...mapGetters(['userProfile', 'loading', 'idToken']),
    ...mapState({
      countriesList: (state) => state.countriesList,
      brokerOriginsNameMap: (state) => state.brokerOriginsNameMap
    }),
    selectedBroker() {
      // get selected broker from local storage
      return localStorage.getItem('selected_broker') || ''
    },
    brokerOrigins() {
      const userOrigins = this.userProfile && this.userProfile.brokerOrigins
      if (!userOrigins) return []

      const defaultBrokerOrigin = ''
      const allowedOrigins = [
        {
          name: 'Default broker',
          value: defaultBrokerOrigin,
          selected: defaultBrokerOrigin === this.selectedBroker
        }
      ]
      if (this.selectedBroker && !userOrigins.includes(this.selectedBroker)) {
        // If the user lost access to the selected broker, reset the selection
        localStorage.setItem('selected_broker', '')
      }
      userOrigins.forEach((origin) => {
        if (this.brokerOriginsNameMap[origin]) {
          allowedOrigins.push({
            name: this.brokerOriginsNameMap[origin],
            value: origin,
            selected: origin === this.selectedBroker
          })
        }
      })
      return allowedOrigins
    },
    subscriptionEnabled() {
      return true
      // return this.idToken && this.idToken.claims && this.idToken.claims.enabledFeatures && this.idToken.claims.enabledFeatures.includes('aspspSubscriptions')
    }
  },
  methods: {
    ...mapActions(['getAspspStatuses', 'getAspspHistoricStatuses', 'getAspspTrafficStatuses', 'getAspspDisruptions', 'subscribe', 'unsubscribe', 'getSubscriptions']),
    async toggleCountry(cc) {
      this.$set(this.expendedCountries, cc, !this.expendedCountries[cc])
      if (this.expendedCountries[cc] && (!this.allHistoricStatuses[cc] || !Object.keys(this.allHistoricStatuses[cc]).length)) {
        this.$set(this.countryLoading, cc, true)
        try {
          const historicResponse = await this.getAspspHistoricStatuses({ broker: this.brokerOrigin, country: cc })

          if (historicResponse && historicResponse.data) {
            const historicStatuses = historicResponse.data
            const last30days = [...Array(30).keys()].map(d => new Date(new Date().setDate(new Date().getDate() - 30 + d)))
            historicStatuses.forEach(status => {
              status.byDate = {}
              last30days.forEach(date => { status.byDate[date.toLocaleDateString('lt')] = { status: 'grey' } })
              status.markers.forEach(m => {
                if (status.byDate[m.date.value] && this.statusOrder[m.status] > this.statusOrder[status.byDate[m.date.value].status]) {
                  status.byDate[m.date.value].status = m.status
                }
              })
              if (!this.allHistoricStatuses[status.country]) this.$set(this.allHistoricStatuses, status.country, {})
              if (!this.allHistoricStatuses[status.country][status.brand]) this.$set(this.allHistoricStatuses[status.country], status.brand, {})
              if (!this.allHistoricStatuses[status.country][status.brand][status.service]) this.$set(this.allHistoricStatuses[status.country][status.brand], status.service, {})
              this.$set(this.allHistoricStatuses[status.country][status.brand][status.service], status.psu_type, status)
            })
          }
        } finally {
          this.$set(this.countryLoading, cc, false)
        }
      }
    },
    handleMouseEnter(itemKey) {
      this.hoveredItem = itemKey
    },
    handleMouseLeave() {
      this.hoveredItem = null
    },
    getTrafficStatuses(cc, brand, service, psuType) {
      if (
        !this.allTrafficStatuses ||
        !this.allTrafficStatuses[cc] ||
        !this.allTrafficStatuses[cc][brand] ||
        !this.allTrafficStatuses[cc][brand][service] ||
        !this.allTrafficStatuses[cc][brand][service][psuType]
      ) {
        return 'LOW'
      }
      return this.allTrafficStatuses[cc][brand][service][psuType].status
    },
    getTrafficStatusesIcon(cc, brand, service, psuType) {
      switch (this.getTrafficStatuses(cc, brand, service, psuType)) {
        case 'HIGH':
          return 'fa-solid fa-battery-full'
        case 'MEDIUM':
          return 'fa-solid fa-battery-half'
        default:
          return 'fa-solid fa-battery-quarter'
      }
    },
    getIssuesCount(cc) {
      if (!this.allStatuses[cc]) {
        return 0
      }
      var issuesCount = 0
      for (const brand of Object.keys(this.allStatuses[cc])) {
        for (const service of Object.keys(this.allStatuses[cc][brand])) {
          for (const psuType of Object.keys(this.allStatuses[cc][brand][service])) {
            if (['red', 'yellow'].findIndex(v => v === this.allStatuses[cc][brand][service][psuType].status) !== -1) {
              issuesCount += 1
            }
          }
        }
      }
      return issuesCount
    },
    getHistoricStatusesByDate(cc, brand, service, psuType) {
      if (
        !this.allHistoricStatuses ||
        !this.allHistoricStatuses[cc] ||
        !this.allHistoricStatuses[cc][brand] ||
        !this.allHistoricStatuses[cc][brand][service] ||
        !this.allHistoricStatuses[cc][brand][service][psuType] ||
        !this.allHistoricStatuses[cc][brand][service][psuType].byDate
      ) {
        return {}
      }
      return this.allHistoricStatuses[cc][brand][service][psuType].byDate
    },
    async toggleBrand(cc, brand) {
      var brandKey = cc + '-' + brand
      this.$set(this.expandedBrands, brandKey, !this.expandedBrands[brandKey])
      if (!this.expandedBrands[brandKey]) return

      var cacheKey = brandKey
      if (this.recentDisruptions[cacheKey] !== undefined) return

      this.$set(this.recentDisruptionsLoading, brandKey, true)
      try {
        var response = await this.getAspspDisruptions({ aspspName: brand, country: cc })
        if (response && response.data) {
          this.$set(this.recentDisruptions, cacheKey, response.data.disruptions)
        } else {
          this.$set(this.recentDisruptions, cacheKey, [])
        }
      } finally {
        this.$set(this.recentDisruptionsLoading, brandKey, false)
      }
    },
    getBrandRecentDisruptions(cc, brand) {
      return this.recentDisruptions[cc + '-' + brand] || null
    },
    async updateStatuses(brokerOrigin) {
      this.aspspLoading = true
      try {
        // Groupping current statuses
        const statusesResponse = await this.getAspspStatuses(brokerOrigin)
        const statuses = (statusesResponse && statusesResponse.data) || []
        const allStatuses = {}
        for (const status of statuses) {
          if (!(status.country in allStatuses)) {
            allStatuses[status.country] = {}
          }
          if (!(status.brand in allStatuses[status.country])) {
            allStatuses[status.country][status.brand] = {}
          }
          if (!(status.service in allStatuses[status.country][status.brand])) {
            allStatuses[status.country][status.brand][status.service] = {}
          }
          allStatuses[status.country][status.brand][status.service][status.psu_type] = status
        }

        const trafficResponse = await this.getAspspTrafficStatuses(brokerOrigin)
        const allTrafficStatusesData = (trafficResponse && trafficResponse.data && trafficResponse.data.traffic_stats) || []
        const trafficStatuses = {}
        for (const status of allTrafficStatusesData) {
          if (!(status.country in trafficStatuses)) {
            trafficStatuses[status.country] = {}
          }
          if (!(status.brand in trafficStatuses[status.country])) {
            trafficStatuses[status.country][status.brand] = {}
          }
          if (!(status.service in trafficStatuses[status.country][status.brand])) {
            trafficStatuses[status.country][status.brand][status.service] = {}
          }
          trafficStatuses[status.country][status.brand][status.service][status.psu_type] = status
        }
        this.allTrafficStatuses = trafficStatuses
        this.allStatuses = allStatuses
        this.allHistoricStatuses = {}
      } finally {
        this.aspspLoading = false
      }
    },
    async getUserSubscriptions(brokerOrigin) {
      // Reset data
      this.isSubscribed = {}
      this.subscriptionIds = {}
      const payload = {
      }
      if (brokerOrigin) {
        payload.broker = brokerOrigin
      }
      const subscriptionsResponse = await this.getSubscriptions(payload)
      const subscriptions = subscriptionsResponse.data.subscriptions

      // Initialize isSubscribed and subscriptionIds for each country
      for (const country in this.countriesList) {
        const subscription = subscriptions.find(
          (sub) => sub.country === country
        )
        // Set isSubscribed[country] to true if a subscription exists,otherwise false
        this.$set(this.isSubscribed, country, !!subscription)

        // Store the subscription ID if a subscription exists
        this.$set(this.subscriptionIds, country, subscription ? subscription.kid : null)
      }
    },
    async handleSubscription(newValue, country) {
      if (!newValue) {
        const payload = {
          country: country,
          broker: this.brokerOrigin
        }
        const response = await this.subscribe(payload)
        // Update subscription ID after subscribing
        this.$set(this.subscriptionIds, country, response.data.subscriptionId)
      } else {
        const subscriptionId = this.subscriptionIds[country]
        const payload = {
          subscriptionId: subscriptionId
        }
        await this.unsubscribe(payload)
        // Clear subscription ID after unsubscribing
        this.$set(this.subscriptionIds, country, null)
      }
    }
  }
}
</script>
<style lang="scss" scoped>
.historic-status {
  cursor: pointer;
}

.flag {
  $flag-size: 1.6rem;

  width: $flag-size;
  height: $flag-size;
}

.traffic-icon {
  color: #8c8c8c;
  font-size: 1.3rem;
  vertical-align: middle;
}

.status-row {
  padding: 0.3rem 0;
}

.status-badge {
  display: inline-block;
  width: 11rem;
  text-align: center;
}

.brand-header {
  cursor: pointer;
  user-select: none;

  &:hover h5 {
    opacity: 0.8;
  }
}

.brand-toggle-arrow {
  font-size: 0.75rem;
  margin-left: 0.5rem;
}

.bg-orange {
  background-color: #fd7e14 !important;
  color: #fff !important;
}

</style>
